import React, { useState, useEffect } from 'react';
import { 
  Banknote, Building2, CheckCircle, Clock, Percent, 
  ChevronDown, ArrowRight, Shield, FileText, Users,
  DollarSign, Calendar, TrendingUp, Zap, Phone, CreditCard,
  BadgeCheck, Star, Award, Loader2, CalendarClock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinanciamientoBancosPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/banks/public`);
      setBanks(response.data);
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  const formatExpirationDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-PY', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return null;
    }
  };

  const isPromoExpired = (dateStr) => {
    if (!dateStr) return false;
    try {
      const date = new Date(dateStr);
      return date < new Date();
    } catch {
      return false;
    }
  };

  const benefits = [
    {
      icon: Percent,
      title: 'Opciones con o sin Intereses',
      description: 'Contamos con planes de financiamiento con intereses preferenciales y opciones sin intereses según el banco y promoción vigente.'
    },
    {
      icon: Calendar,
      title: 'Plazos Flexibles',
      description: 'Elegí el plazo que mejor se adapte a tu presupuesto. Cuotas accesibles para todo tipo de proyectos.'
    },
    {
      icon: Shield,
      title: 'Sin Garantía Adicional',
      description: 'El sistema solar queda como garantía del préstamo. No necesitás hipotecar tu casa.'
    },
    {
      icon: BadgeCheck,
      title: 'Pre-aprobado',
      description: 'Zenex gestiona todo el proceso. Llegás al banco con la documentación lista y pre-aprobada.'
    },
    {
      icon: TrendingUp,
      title: 'Cuota vs Ahorro',
      description: 'Tu cuota mensual será similar o menor a tu ahorro en electricidad. El sistema se paga solo.'
    },
    {
      icon: Award,
      title: 'Beneficios Fiscales',
      description: 'Combiná el crédito con beneficios fiscales para maximizar el retorno de tu inversión.'
    }
  ];

  const faqs = [
    {
      question: '¿Puedo acceder al crédito si no soy cliente del banco?',
      answer: 'Sí, podés abrir cuenta en cualquiera de nuestros bancos aliados como parte del proceso. Zenex te acompaña en todo el trámite.'
    },
    {
      question: '¿Qué documentos necesito para el crédito bancario?',
      answer: 'Cédula de identidad, comprobantes de ingresos (últimos 3 meses), certificado de trabajo o RUC activo, y factura de electricidad. El banco puede solicitar documentación adicional según el monto.'
    },
    {
      question: '¿El crédito es solo para el sistema solar o puedo incluir otros servicios?',
      answer: 'El crédito cubre todo el proyecto: paneles, inversor, estructura, instalación, trámites con ANDE y garantías. Todo en un solo financiamiento.'
    },
    {
      question: '¿Cuál es el monto mínimo y máximo que puedo financiar?',
      answer: 'Depende del banco, pero generalmente desde USD 3,000 hasta USD 100,000 o más para proyectos industriales. Consultanos para proyectos de mayor envergadura.'
    },
    {
      question: '¿Puedo refinanciar o extender el plazo después?',
      answer: 'Sí, la mayoría de los bancos permiten refinanciar el crédito. También podés adelantar pagos sin penalidad para reducir el plazo o la cuota.'
    },
    {
      question: '¿Zenex cobra comisión por gestionar el crédito?',
      answer: 'No, la gestión del crédito es un servicio gratuito que ofrecemos a nuestros clientes. Nuestro interés es facilitarte el acceso a la energía solar.'
    },
    {
      question: '¿Hay opciones sin intereses?',
      answer: 'Sí, algunos de nuestros bancos aliados ofrecen promociones con cuotas sin intereses. Consultá con nuestros asesores para conocer las promociones vigentes.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="financiamiento-bancos-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-32 bg-gradient-to-br from-blue-900 via-blue-800 to-emerald-800 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <Banknote className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Créditos Bancarios</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Planes a Cuotas con
            <span className="block text-emerald-400">Bancos Aliados</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl mb-4">
            Accedé a créditos preferenciales con nuestros bancos aliados. 
            Tasas especiales para energía renovable y plazos extendidos.
          </p>
          
          <p className="text-lg text-emerald-400 font-medium mb-8">
            Opciones con y sin intereses disponibles según promociones vigentes.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg shadow-emerald-500/30"
            >
              Simular Mi Crédito
              <ArrowRight className="w-5 h-5" />
            </button>
            <a 
              href="https://wa.me/595991344956?text=Hola,%20me%20interesa%20el%20financiamiento%20con%20bancos"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
            >
              <Phone className="w-5 h-5" />
              Asesoramiento Gratis
            </a>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-16 max-w-2xl">
            {[
              { value: `${banks.length || '6'}+`, label: 'Bancos aliados' },
              { value: 'Con/Sin', label: 'Intereses' },
              { value: '$0', label: 'Costo de gestión' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
                <p className="text-3xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-sm text-zinc-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Banks Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Nuestros Bancos Aliados
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Trabajamos con los principales bancos de Paraguay para ofrecerte las mejores condiciones.
            </p>
          </div>

          {loadingBanks ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : banks.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {banks.map((bank, index) => {
                const promoExpired = isPromoExpired(bank.promo_expiration_date);
                const formattedDate = formatExpirationDate(bank.promo_expiration_date);
                
                return (
                  <div 
                    key={bank.id || index}
                    className={`relative p-6 rounded-2xl transition-all duration-300 ${
                      bank.is_highlighted 
                        ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/30' 
                        : 'bg-white border border-zinc-200 hover:shadow-lg hover:border-blue-200'
                    }`}
                  >
                    {bank.is_highlighted && (
                      <div className="absolute -top-3 right-4 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        RECOMENDADO
                      </div>
                    )}
                    
                    <div className="h-16 mb-4 flex items-center gap-3">
                      {bank.logo_url && (
                        <img 
                          src={bank.logo_url} 
                          alt={bank.name}
                          className="h-12 max-w-[120px] object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      {!bank.logo_url && (
                        <div className="flex items-center justify-center">
                          <Building2 className={`w-10 h-10 ${bank.is_highlighted ? 'text-white' : 'text-blue-600'}`} />
                        </div>
                      )}
                      <span className={`text-xl font-bold ${bank.is_highlighted ? 'text-white' : 'text-zinc-900'}`}>
                        {bank.name}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {bank.max_months && (
                        <div>
                          <p className={`text-sm ${bank.is_highlighted ? 'text-blue-200' : 'text-zinc-500'}`}>Hasta</p>
                          <p className={`text-2xl font-bold ${bank.is_highlighted ? 'text-white' : 'text-blue-600'}`}>
                            {bank.max_months} cuotas
                          </p>
                        </div>
                      )}
                      {bank.min_rate && (
                        <div>
                          <p className={`text-sm ${bank.is_highlighted ? 'text-blue-200' : 'text-zinc-500'}`}>Tasa desde</p>
                          <p className={`text-2xl font-bold ${bank.is_highlighted ? 'text-white' : 'text-blue-600'}`}>
                            {bank.min_rate}
                          </p>
                        </div>
                      )}
                    </div>

                    {bank.has_interest_free_option && (
                      <div className={`mb-4 px-3 py-2 rounded-lg flex items-center gap-2 ${
                        bank.is_highlighted ? 'bg-emerald-500/30' : 'bg-emerald-50'
                      }`}>
                        <BadgeCheck className={`w-4 h-4 ${bank.is_highlighted ? 'text-emerald-300' : 'text-emerald-600'}`} />
                        <span className={`text-sm font-medium ${bank.is_highlighted ? 'text-emerald-200' : 'text-emerald-700'}`}>
                          Opción sin intereses disponible
                        </span>
                      </div>
                    )}
                    
                    <ul className="space-y-2 mb-4">
                      {bank.benefits && bank.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle className={`w-4 h-4 ${bank.is_highlighted ? 'text-emerald-300' : 'text-emerald-500'}`} />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    {formattedDate && (
                      <div className={`mt-4 pt-4 border-t ${
                        bank.is_highlighted ? 'border-blue-500' : 'border-zinc-200'
                      }`}>
                        <div className={`flex items-center gap-2 text-sm ${
                          promoExpired 
                            ? 'text-red-500' 
                            : bank.is_highlighted ? 'text-blue-200' : 'text-zinc-500'
                        }`}>
                          <CalendarClock className="w-4 h-4" />
                          <span>
                            {promoExpired 
                              ? 'Promoción vencida' 
                              : `Promo válida hasta: ${formattedDate}`
                            }
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
              <p>Próximamente agregaremos nuestros bancos aliados</p>
            </div>
          )}

          <p className="text-center text-zinc-500 mt-8 text-sm">
            * Las tasas y condiciones pueden variar según perfil crediticio y políticas de cada banco.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Ventajas del Crédito Bancario
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Para proyectos de mayor envergadura, el crédito bancario ofrece beneficios únicos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="p-6 bg-zinc-50 rounded-xl hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                    <Icon className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-zinc-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden bg-white"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Hacé realidad tu proyecto solar
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Te ayudamos a gestionar el crédito bancario sin costo adicional.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20quiero%20gestionar%20un%20crédito%20bancario%20para%20paneles%20solares"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Comenzar Ahora
            </a>
            <button 
              onClick={() => navigate('/calculadora')}
              className="px-8 py-4 bg-blue-800 text-white font-semibold rounded-xl hover:bg-blue-900 transition-all flex items-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Simular Crédito
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default FinanciamientoBancosPage;
